import { useEffect, useRef, useState } from "react";
import { Star, ShieldCheck, Clock, Building2, Users } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

/**
 * Premium trust strip for the landing page.
 * - Animated counter (0 → 2000+) triggered when in-view.
 * - Micro-badges (5★ rating, 24/7 destek, KVKK/TR bankacılık, aktif dükkanlar).
 * - Fully i18n-aware.
 */
export default function TrustBar() {
  const { t } = useI18n();
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current || started.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const TARGET = 2000;
            const DURATION = 1800; // ms
            const start = performance.now();
            const step = (now) => {
              const p = Math.min(1, (now - start) / DURATION);
              // easeOutCubic
              const eased = 1 - Math.pow(1 - p, 3);
              setCount(Math.round(TARGET * eased));
              if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative z-10 border-y border-white/8"
      style={{ background: "linear-gradient(180deg, #050505 0%, #080808 100%)" }}
      data-testid="trust-bar"
    >
      {/* Subtle gold accents */}
      <div className="absolute inset-0 pointer-events-none opacity-40" style={{
        background: "radial-gradient(circle at 50% 0%, rgba(212,175,55,0.10), transparent 55%)",
      }} />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-10 sm:py-14">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          {/* LEFT — Big animated proof */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-4">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
                      style={{ background: "var(--gold)" }}></span>
                <span className="relative inline-flex rounded-full h-2 w-2"
                      style={{ background: "var(--gold)" }}></span>
              </span>
              <div className="label-caps" data-testid="trust-badge-label">
                {t("trust.badge")}
              </div>
            </div>
            <div className="flex items-baseline gap-3 flex-wrap" data-testid="trust-counter-wrapper">
              <span
                className="font-display font-light leading-none"
                style={{
                  color: "var(--gold)",
                  fontSize: "clamp(3rem, 7vw, 5.75rem)",
                  textShadow: "0 0 40px rgba(212,175,55,0.35)",
                }}
                data-testid="trust-counter"
              >
                {count.toLocaleString("tr-TR")}
                <span className="text-[--gold]/80">+</span>
              </span>
              <div className="hidden sm:flex items-center gap-1 pb-2">
                {[0,1,2,3,4].map(i => (
                  <Star key={i} size={16} className="fill-[--gold]" style={{ color: "var(--gold)" }} />
                ))}
                <span className="text-[11px] font-mono tracking-wider text-neutral-400 ml-2">4.9 / 5</span>
              </div>
            </div>
            <p
              className="text-neutral-300 text-base sm:text-lg lg:text-xl leading-relaxed mt-4 max-w-2xl font-light"
              data-testid="trust-headline"
            >
              {t("trust.headline")}
            </p>
            <p className="text-neutral-500 text-xs sm:text-sm mt-3 font-mono tracking-wide max-w-xl">
              {t("trust.subline")}
            </p>
          </div>

          {/* RIGHT — Mini trust chips */}
          <div className="lg:col-span-5" data-testid="trust-chips">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {[
                { icon: Building2, k: "trust.chip1_v", l: "trust.chip1_l" },
                { icon: Users,     k: "trust.chip2_v", l: "trust.chip2_l" },
                { icon: ShieldCheck, k: "trust.chip3_v", l: "trust.chip3_l" },
                { icon: Clock,     k: "trust.chip4_v", l: "trust.chip4_l" },
              ].map((c, i) => (
                <div
                  key={i}
                  className="group relative border border-white/8 bg-[#0a0a0a]/70 backdrop-blur p-4 hover:border-[--gold]/40 transition-all overflow-hidden"
                  data-testid={`trust-chip-${i}`}
                >
                  <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                       style={{ background: "radial-gradient(circle, rgba(212,175,55,0.15), transparent 70%)" }} />
                  <c.icon size={16} style={{ color: "var(--gold)" }} className="mb-2" />
                  <div className="font-display text-xl sm:text-2xl leading-tight" style={{ color: "var(--gold)" }}>
                    {t(c.k)}
                  </div>
                  <div className="label-caps mt-1 !text-[9px]">{t(c.l)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
